import { createRouter, createWebHistory } from 'vue-router';
import LoginPage from '../components/LoginPage.vue';
import DevicesPage from "@/components/DevicesPage.vue";
import DeviceDettailsPage from "@/components/DeviceDettailsPage.vue";
import MpidPage from "@/components/MpidPage.vue";
import UserSettingsPage from "@/components/UserSettingsPage.vue";
import DriveByPage from "@/components/DriveByPage.vue";
import ReadingDevicesPage from "@/components/ReadingDevicesPage.vue";
import MpidDetailsPage from '@/components/MpidDetailsPage.vue'
import CollectingReadings from '@/components/ColletingReadings.vue'
import LoraDashboard from "@/components/LoraDashboard.vue";
import DashboardPage from "@/components/DashboardPage.vue";
import DlmsTaskingPage from "@/components/DlmsTaskingPage.vue";


// --- Helper function to check authentication status ---
function isAuthenticated() {
    const token = localStorage.getItem('access_token');
    const expiresAtString = localStorage.getItem('token_expires_at'); // Renamed for clarity

    if (!token || !expiresAtString) {
        // No token or expiration time string found
        return false;
    }


    const expirationTime = Date.parse(expiresAtString);

    if (isNaN(expirationTime)) {
        console.error("Invalid token expiration time format in localStorage:", expiresAtString);
        return false;
    }

    // Check if the current time is past the expiration time
    // Date.now() is also in milliseconds
    const isValid = Date.now() < expirationTime;
    // console.log("Token check:", { tokenExists: !!token, expiresAtString, expirationTime, now: Date.now(), isValid }); // Debugging log
    return isValid;
}

// --- Helper function to clear auth data ---
function clearAuthData() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('user_info');
    localStorage.removeItem('user_role');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('accessible_tenants');
    localStorage.removeItem('permissions');
    localStorage.removeItem('remember_user');
    console.log("Authentication data cleared."); // Simplified message
}

const routes = [
    {
        path: '/',
        redirect: '/login'
    },
    {
        path: '/login',
        name: 'Login',
        component: LoginPage
    },
    {
        path: '/devices',
        name: 'Devices',
        component: DevicesPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/devices/:ldn',
        name: 'Device Profile',
        component: DeviceDettailsPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/meteringpoints',
        name: 'Metering Points',
        component: MpidPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/reading-devices',
        name: 'Reading Devices',
        component: ReadingDevicesPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/driveby',
        name: 'Drive By',
        component: DriveByPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/dashboard',
        name: 'Dashboard',
        component: DashboardPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/loradashboard',
        name: 'Lora Dashboard',
        component: LoraDashboard,
        meta: { requiresAuth: true }
    },
    {
        path: '/dlms',
        name: 'DLMS',
        component: DlmsTaskingPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/collecting-readings/:routeName',
        name: 'Collecting Readings',
        component: CollectingReadings,
        props: true,
        meta: { requiresAuth: true }
    },
    {
        path: '/user-settings',
        name: 'User Settings',
        component: UserSettingsPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/mpids/:mpid',
        name: 'MpidDetails',
        component: MpidDetailsPage,
        props: true,
        meta: { requiresAuth: true }
    },
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes
});

// --- Global Navigation Guard ---
router.beforeEach((to, from, next) => {
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
    const loggedIn = isAuthenticated(); // Check current auth status

    // console.log("Navigation Guard:", { path: to.path, requiresAuth, loggedIn }); // Debugging log

    if (requiresAuth && !loggedIn) {
        // If route needs auth but user isn't logged in (or token expired)
        console.log(`Navigation blocked to "${to.path}". User not authenticated or token expired. Redirecting to login.`);
        // Check if we are already trying to navigate away from login, avoid clearing if we just landed
        if (from.name !== 'Login') {
            clearAuthData(); // Clear stale data only if navigating *away* from a non-login page
        }
        // Prevent redirect loop if already going to login
        if (to.name !== 'Login') {
            next('/login');
        } else {
            next(); // Already going to login, allow it.
        }
    } else if (to.name === 'Login' && loggedIn) {
        // If user is logged in and tries to go to Login page, redirect to main app page
        console.log(`User already authenticated. Redirecting from Login to Devices.`);
        next('/devices');
    } else {
        // Otherwise (public route, or protected route with valid token), allow navigation
        next();
    }
});

export default router;
